import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, MenuItem, TableCell, TableRow, TextField, Typography } from '@mui/material';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { DataTable, useAppSnackbar } from '../../common/TableDesign';
import { CustomerPicker, LocationPicker, SupplierPicker } from '../LedgerPickers';
import { getErrorMessage, unwrap } from './TaxShared';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { Download as DownloadIcon } from '@mui/icons-material';

const REPORTS = [
  { value: 'vat', label: 'VAT Report' },
  { value: 'wht', label: 'WHT Report' },
  { value: 'fx', label: 'FX Report' },
];

// Statutory tax reports (verified against TaxReportsController): VAT
// input/output, WHT payable/receivable and FX realised/unrealised gains -
// all over a required from/to date range with optional detail rows.
const TaxReportsTab = () => {
  const { can } = useAccessControl();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const [reportType, setReportType] = useState('vat');
  const [filters, setFilters] = useState({
    from_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    vat_type: 'BOTH',
    report_type: 'BOTH',
    include_details: false,
    location_id: '',
    customer_id: '',
    supplier_id: '',
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!can(RESOURCES.LEDGER.TAX, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Tax Reports.</Alert>;
  }

  const runReport = async () => {
    if (!filters.from_date || !filters.to_date) {
      showSnackbar('From and to dates are required', 'error');
      return;
    }
    try {
      setLoading(true);
      const base = { from_date: filters.from_date, to_date: filters.to_date, include_details: filters.include_details };
      let res;
      if (reportType === 'vat') {
        res = await ledgerAPI.getVatReport({
          ...base,
          vat_type: filters.vat_type,
          ...(filters.location_id ? { location_id: filters.location_id } : {}),
        });
      } else if (reportType === 'wht') {
        res = await ledgerAPI.getWhtReport({
          ...base,
          report_type: filters.report_type,
          ...(filters.customer_id ? { customer_id: filters.customer_id } : {}),
          ...(filters.supplier_id ? { supplier_id: filters.supplier_id } : {}),
        });
      } else {
        res = await ledgerAPI.getFxReport({ ...base, report_type: filters.report_type });
      }
      setReport(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch report'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const rows = Array.isArray(report?.rows) ? report.rows : (Array.isArray(report?.lines) ? report.lines : (Array.isArray(report?.details) ? report.details : []));
  const totals = report?.totals || report?.summary || {};

  const exportCsv = () => {
    downloadCsv(
      `tax-${reportType}-report.csv`,
      ['Description', 'Amount'],
      rows.map((r) => [r.description || r.label || r.line_type || '', r.amount ?? r.total ?? ''])
    );
  };

  const amountOf = (r) => r.amount ?? r.total ?? r.tax_amount ?? r.net_amount;

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="flex-start">
            <TextField size="small" select label="Report" value={reportType} onChange={(e) => { setReportType(e.target.value); setReport(null); }} sx={{ width: 200 }}>
              {REPORTS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small" type="date" label="From" value={filters.from_date}
              onChange={(e) => setFilters((p) => ({ ...p, from_date: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ width: 160 }} required
            />
            <TextField
              size="small" type="date" label="To" value={filters.to_date}
              onChange={(e) => setFilters((p) => ({ ...p, to_date: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ width: 160 }} required
            />
            {reportType === 'vat' && (
              <>
                <TextField size="small" select label="VAT Side" value={filters.vat_type} onChange={(e) => setFilters((p) => ({ ...p, vat_type: e.target.value }))} sx={{ width: 150 }}>
                  {['INPUT', 'OUTPUT', 'BOTH'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <Box sx={{ width: 220 }}>
                  <LocationPicker label="Location (optional)" value={filters.location_id} onChange={(id) => setFilters((p) => ({ ...p, location_id: id }))} />
                </Box>
              </>
            )}
            {reportType === 'wht' && (
              <>
                <TextField size="small" select label="Side" value={filters.report_type} onChange={(e) => setFilters((p) => ({ ...p, report_type: e.target.value }))} sx={{ width: 170 }}>
                  {['PAYABLE', 'RECEIVABLE', 'BOTH'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <Box sx={{ width: 200 }}>
                  <CustomerPicker value={filters.customer_id} onChange={(id) => setFilters((p) => ({ ...p, customer_id: id }))} />
                </Box>
                <Box sx={{ width: 200 }}>
                  <SupplierPicker value={filters.supplier_id} onChange={(id) => setFilters((p) => ({ ...p, supplier_id: id }))} />
                </Box>
              </>
            )}
            {reportType === 'fx' && (
              <TextField size="small" select label="Gains" value={filters.report_type} onChange={(e) => setFilters((p) => ({ ...p, report_type: e.target.value }))} sx={{ width: 170 }}>
                {['REALIZED', 'UNREALIZED', 'BOTH'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            )}
          </Box>
          <Box display="flex" gap={1} sx={{ mt: 2 }}>
            <Button
              size="small"
              variant={filters.include_details ? 'contained' : 'outlined'}
              onClick={() => setFilters((p) => ({ ...p, include_details: !p.include_details }))}
            >
              {filters.include_details ? 'Detail rows: on' : 'Detail rows: off'}
            </Button>
            <Button variant="contained" onClick={runReport} disabled={loading}>
              {loading ? 'Loading...' : 'Run Report'}
            </Button>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!rows.length}>
              Export CSV
            </Button>
          </Box>
          {reportType === 'vat' && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Requires the default VAT input/output accounts under Accounting Settings -&gt; Policies
              (accounting_default_vat_input_account_id / accounting_default_vat_output_account_id).
            </Typography>
          )}
          {reportType === 'fx' && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Requires the FX gain/loss accounts under Accounting Settings -&gt; Policies
              (fx_gain_account_id / fx_loss_account_id).
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {!report ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              Choose a report and run it.
            </Typography>
          ) : (
            <>
              <DataTable
                columns={['Line', 'Amount']}
                data={rows}
                loading={loading}
                emptyMessage="No rows for this period."
                renderRow={(r, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <strong>{r.description || r.label || r.line_type || r.currency_code || `Row ${i + 1}`}</strong>
                      {r.currency_code && r.description ? ` (${r.currency_code})` : ''}
                    </TableCell>
                    <TableCell align="right">{formatAccountingAmount(amountOf(r))}</TableCell>
                  </TableRow>
                )}
              />
              {Object.keys(totals).length > 0 && (
                <Box display="flex" justifyContent="flex-end" flexWrap="wrap" gap={3} sx={{ mt: 1 }}>
                  {Object.entries(totals).slice(0, 6).map(([k, v]) => (
                    <Typography key={k} variant="body2">
                      {k.replace(/_/g, ' ')}: <strong>{typeof v === 'number' ? formatAccountingAmount(v) : String(v)}</strong>
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {SnackbarComponent}
    </Box>
  );
};

export default TaxReportsTab;